import { useState, useEffect, useRef } from "react";
import { TextField, Button, CircularProgress, Box, Typography } from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import BlockIcon from "@mui/icons-material/Block";
import useFederationVerification, {
  FederationStatus,
} from "../../../hooks/useFederationVerification";
import FederationAffiliationBox from "../federationAffiliation";
import {
  Container,
  Title,
  Subtitle,
  CpfRow,
  ResultBox,
  ResultContent,
  ResultTitle,
  ResultDetail,
} from "./styles";

// Mapeamento faixa string (federação) -> int (torneio fácil)
const BELT_STRING_TO_INT: Record<string, number> = {
  branca: 1, cinza: 2, amarela: 3, laranja: 4, verde: 5,
  azul: 6, roxa: 7, marrom: 8, preta: 9,
  coral: 10, "coral-vermelha": 11, vermelha: 12,
};

const BELT_INT_TO_STRING: Record<number, string> = {
  1: "Branca", 2: "Cinza", 3: "Amarela", 4: "Laranja", 5: "Verde",
  6: "Azul", 7: "Roxa", 8: "Marrom", 9: "Preta",
  10: "Coral", 11: "Coral-Vermelha", 12: "Vermelha",
};

interface FederationVerificationBoxProps {
  federationSlug: string;
  federationName: string;
  fighterId?: number;
  fighterCpf?: string | null;
  fighterBelt?: number;
  fighterGym?: string;
  token: string | null;
  onVerified?: (data: {
    affiliationId: string;
    athleteUuid?: string;
    belt?: string;
    academy?: { id: string; name: string } | null;
    expiresAt?: string;
    cpf: string;
  }) => void;
  onNotFound?: () => void;
  onSyncData?: (updates: { belt?: number; gym?: string; genre?: string }) => Promise<void>;
  onCpfUpdated?: (newCpf: string) => Promise<void>;
  isDependentAthlete?: boolean;
  disabled?: boolean;
  // Dados do atleta para formulário de filiação
  athleteName?: string;
  athleteEmail?: string;
  athletePhone?: string;
  athleteBirthDate?: string;
  // Dados do dono da conta (responsável quando atleta é menor)
  accountName?: string;
  accountCpf?: string;
  accountPhone?: string;
  accountEmail?: string;
}

const formatCpf = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const FederationVerificationBox = ({
  federationSlug,
  federationName,
  fighterId,
  fighterCpf,
  fighterBelt,
  fighterGym,
  token,
  onVerified,
  onNotFound,
  onSyncData,
  onCpfUpdated,
  isDependentAthlete = false,
  disabled = false,
  athleteName,
  athleteEmail,
  athletePhone,
  athleteBirthDate,
  accountName,
  accountCpf,
  accountPhone,
  accountEmail,
}: FederationVerificationBoxProps) => {
  const [cpf, setCpf] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncedFields, setSyncedFields] = useState<string[]>([]);
  const [manualCpfMode, setManualCpfMode] = useState(false);
  const [showAffiliationForm, setShowAffiliationForm] = useState(false);
  // Quando fighter não tem CPF, perguntar se já possui filiação antes de mostrar campo CPF
  const [initialChoice, setInitialChoice] = useState<"pending" | "has_affiliation" | "no_affiliation">("pending");
  const { status, result, errorMessage, verify, reset } =
    useFederationVerification();
  const autoVerifiedRef = useRef<string | null>(null);
  const pendingPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncedRef = useRef(false);

  // Auto-sincronizar dados divergentes quando verificação retorna
  // A federacao e fonte de verdade para campos compartilhados
  const autoSyncIfNeeded = async (verifyResult: any) => {
    if (!onSyncData || syncedRef.current) return;

    const updates: { belt?: number; gym?: string; genre?: string } = {};
    const fields: string[] = [];

    // Comparar faixa
    if (verifyResult.belt && fighterBelt !== undefined) {
      const fedBeltInt = BELT_STRING_TO_INT[verifyResult.belt.toLowerCase()];
      if (fedBeltInt && fedBeltInt !== fighterBelt) {
        updates.belt = fedBeltInt;
        fields.push("Faixa");
      }
    }

    // Comparar academia
    if (verifyResult.academy?.name && fighterGym) {
      const normalize = (s: string) => s.trim().toLowerCase();
      if (normalize(verifyResult.academy.name) !== normalize(fighterGym)) {
        updates.gym = verifyResult.academy.name;
        fields.push("Academia");
      }
    }

    // Sincronizar campos custom da federacao (genero, telefone, etc)
    if (verifyResult.customFields) {
      const cf = verifyResult.customFields as Record<string, unknown>;

      // Genero
      const fedGenero = cf.genero || cf.sexo || cf.gender;
      if (fedGenero) {
        const s = String(fedGenero).trim().toLowerCase();
        let normalized = "";
        if (s === "masculino" || s === "m") normalized = "Masculino";
        else if (s === "feminino" || s === "f") normalized = "Feminino";
        if (normalized) {
          updates.genre = normalized;
          fields.push("Gênero");
        }
      }
    }

    if (fields.length === 0) return;

    syncedRef.current = true;
    setSyncing(true);
    try {
      await onSyncData(updates);
      setSyncedFields(fields);
    } catch {
      // Erro tratado pelo callback
    } finally {
      setSyncing(false);
    }
  };

  // Auto-preencher e verificar automaticamente quando fighterCpf muda
  useEffect(() => {
    let isCancelled = false;

    setSyncedFields([]);
    setShowAffiliationForm(false);
    setInitialChoice("pending");
    syncedRef.current = false;
    if (fighterCpf && fighterCpf.replace(/\D/g, "").length === 11) {
      const digits = fighterCpf.replace(/\D/g, "");
      setCpf(formatCpf(digits));

      // Evitar re-verificar o mesmo CPF
      if (autoVerifiedRef.current === digits) return;
      autoVerifiedRef.current = digits;

      // Verificar automaticamente
      (async () => {
        const res = await verify(federationSlug, digits, fighterId, token, true);
        if (isCancelled) return;
        if (res?.verified && res.affiliationId) {
          await autoSyncIfNeeded(res);
          if (isCancelled) return;
          onVerified?.({
            affiliationId: res.affiliationId,
            athleteUuid: res.athleteUuid,
            belt: res.belt,
            academy: res.academy,
            expiresAt: res.expiresAt,
            cpf: digits,
          });
        } else if (res && !res.verified && res.reason === "NOT_FOUND") {
          onNotFound?.();
        }
      })();
    } else {
      // Sem CPF cadastrado — limpar para digitação manual
      setCpf("");
      autoVerifiedRef.current = null;
      setManualCpfMode(false);
      reset();
    }

    return () => { isCancelled = true; };
  }, [fighterCpf, fighterId]);

  const hasCpfFromFighter = !!(fighterCpf && fighterCpf.replace(/\D/g, "").length === 11);

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCpf(e.target.value);
    setCpf(formatted);
    autoVerifiedRef.current = null;
    setShowAffiliationForm(false);
    syncedRef.current = false;
    setSyncedFields([]);

    // Reset verification when CPF changes
    if (status !== "idle" && status !== "loading") {
      reset();
    }
  };

  const handleVerify = async () => {
    const digits = cpf.replace(/\D/g, "");
    if (digits.length !== 11) return;

    syncedRef.current = false;
    setSyncedFields([]);
    const res = await verify(federationSlug, digits, fighterId, token, true);

    if (res?.verified && res.affiliationId) {
      await autoSyncIfNeeded(res);
      onVerified?.({
        affiliationId: res.affiliationId,
        athleteUuid: res.athleteUuid,
        belt: res.belt,
        academy: res.academy,
        expiresAt: res.expiresAt,
        cpf: digits,
      });
    } else if (res && !res.verified && res.reason === "NOT_FOUND") {
      onNotFound?.();
    }
  };

  // Quando a filiação ficar ativa (callback do FederationAffiliationBox),
  // re-verificar automaticamente com o CPF usado na filiação
  const handleAffiliationActive = async (affiliationCpf?: string) => {
    setShowAffiliationForm(false);
    // Usar o CPF que veio da filiação (pode ser diferente do original)
    const digits = (affiliationCpf || cpf).replace(/\D/g, "");
    if (digits.length === 11) {
      // Se o CPF mudou (filiou com CPF diferente do fighter), atualizar no fighter primeiro
      const originalCpf = (fighterCpf || "").replace(/\D/g, "");
      if (digits !== originalCpf && onCpfUpdated) {
        try {
          await onCpfUpdated(digits);
        } catch {
          // Se falhar, continuar mesmo assim
        }
      }

      setCpf(formatCpf(digits));
      autoVerifiedRef.current = null;
      syncedRef.current = false;
      const res = await verify(federationSlug, digits, fighterId, token, true);
      if (res?.verified && res.affiliationId) {
        await autoSyncIfNeeded(res);
        onVerified?.({
          affiliationId: res.affiliationId,
          athleteUuid: res.athleteUuid,
          belt: res.belt,
          academy: res.academy,
          expiresAt: res.expiresAt,
          cpf: digits,
        });
      }
    }
  };

  const canVerify =
    cpf.replace(/\D/g, "").length === 11 &&
    status !== "loading" &&
    status !== "verified" &&
    !disabled;

  // Extrair sub-status da filiação (PENDING, AWAITING_PAYMENT, REJECTED)
  const affiliationSubStatus = result?.status;

  // Polling automático quando status é PENDING (aguardando aprovação)
  useEffect(() => {
    if (status === "not_active" && affiliationSubStatus === "PENDING") {
      let isCancelled = false;
      let errorCount = 0;

      pendingPollingRef.current = setInterval(async () => {
        if (isCancelled) return;
        const digits = cpf.replace(/\D/g, "");
        if (digits.length !== 11) return;
        try {
          const res = await verify(federationSlug, digits, fighterId, token, true);
          errorCount = 0;
          if (res?.verified && res.affiliationId) {
            onVerified?.({
              affiliationId: res.affiliationId,
              athleteUuid: res.athleteUuid,
              belt: res.belt,
              academy: res.academy,
              expiresAt: res.expiresAt,
              cpf: digits,
            });
          }
        } catch (err: any) {
          if (err?.response?.status === 401) {
            if (pendingPollingRef.current) {
              clearInterval(pendingPollingRef.current);
              pendingPollingRef.current = null;
            }
            return;
          }
          errorCount++;
          if (errorCount >= 5 && pendingPollingRef.current) {
            clearInterval(pendingPollingRef.current);
            pendingPollingRef.current = null;
          }
        }
      }, 30000);

      return () => {
        isCancelled = true;
        if (pendingPollingRef.current) {
          clearInterval(pendingPollingRef.current);
          pendingPollingRef.current = null;
        }
      };
    }

    return () => {
      if (pendingPollingRef.current) {
        clearInterval(pendingPollingRef.current);
        pendingPollingRef.current = null;
      }
    };
  }, [status, affiliationSubStatus]);

  // Quando fighter não tem CPF e ainda não escolheu, mostrar pergunta inicial
  if (!hasCpfFromFighter && initialChoice === "pending" && status === "idle") {
    return (
      <Container>
        <Title>
          <VerifiedIcon sx={{ fontSize: 18, color: "var(--navy, #1a2252)" }} />
          Filiação — {federationName}
        </Title>
        <Subtitle>
          Este torneio exige filiação ativa na federação. O atleta já possui filiação?
        </Subtitle>
        <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setInitialChoice("has_affiliation");
              setManualCpfMode(true);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "var(--navy, #1a2252)",
              "&:hover": { bgcolor: "var(--navy-light, #243070)" },
            }}
          >
            Sim, já sou filiado
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setInitialChoice("no_affiliation");
              setShowAffiliationForm(true);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "var(--navy, #1a2252)",
              borderColor: "var(--navy, #1a2252)",
              "&:hover": { borderColor: "var(--navy-light, #243070)", bgcolor: "#f0f4ff" },
            }}
          >
            Não, quero me filiar
          </Button>
        </Box>
      </Container>
    );
  }

  // Se o formulário de filiação está visível, renderizar ele
  if (showAffiliationForm) {
    return (
      <FederationAffiliationBox
        federationSlug={federationSlug}
        federationName={federationName}
        token={token}
        athleteName={athleteName || ""}
        athleteCpf={cpf}
        athleteEmail={athleteEmail || ""}
        athletePhone={athletePhone}
        athleteBirthDate={athleteBirthDate}
        athleteBelt={fighterBelt ? BELT_INT_TO_STRING[fighterBelt]?.toLowerCase() : undefined}
        athleteGym={fighterGym}
        fighterId={fighterId}
        existingAffiliationId={result?.affiliationId}
        existingStatus={affiliationSubStatus}
        isRenewal={status === "expired"}
        onAffiliationActive={handleAffiliationActive}
        onCpfChanged={async (newCpf: string) => {
          // Atualizar CPF no state local e notificar o enrollment
          setCpf(formatCpf(newCpf));
          autoVerifiedRef.current = null;
          if (onCpfUpdated) {
            await onCpfUpdated(newCpf);
          }
        }}
        isDependentAthlete={isDependentAthlete}
        accountName={accountName}
        accountCpf={accountCpf}
        accountPhone={accountPhone}
        accountEmail={accountEmail}
      />
    );
  }

  return (
    <Container>
      <Title>
        <VerifiedIcon sx={{ fontSize: 18, color: "var(--navy, #1a2252)" }} />
        Verificar filiação — {federationName}
      </Title>
      <Subtitle>
        {hasCpfFromFighter
          ? "Verificando filiação automaticamente com o CPF cadastrado do atleta."
          : "Este torneio exige filiação ativa. Informe o CPF do atleta para verificar."}
      </Subtitle>

      <CpfRow>
        <TextField
          label="CPF do Atleta"
          value={cpf}
          onChange={handleCpfChange}
          placeholder="000.000.000-00"
          size="small"
          disabled={disabled || status === "verified" || (hasCpfFromFighter && !manualCpfMode)}
          sx={{ flex: 1, minWidth: 200 }}
          inputProps={{ maxLength: 14 }}
        />
        {(!hasCpfFromFighter || manualCpfMode) && (
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={!canVerify}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              height: 40,
              bgcolor: "var(--navy, #1a2252)",
              "&:hover": { bgcolor: "var(--navy-light, #243070)" },
            }}
          >
            {status === "loading" ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Verificar"
            )}
          </Button>
        )}
      </CpfRow>

      {/* Loading sync */}
      {syncing && (
        <ResultBox $variant="info">
          <CircularProgress size={18} sx={{ color: "#1e40af", mt: "2px" }} />
          <ResultContent>
            <ResultTitle>Atualizando dados com a federação...</ResultTitle>
          </ResultContent>
        </ResultBox>
      )}

      {status === "verified" && result && !syncing && (
        <>
          <ResultBox $variant="success">
            <VerifiedIcon sx={{ fontSize: 22, color: "#16a34a", mt: "2px" }} />
            <ResultContent>
              <ResultTitle>Filiação verificada</ResultTitle>
              <ResultDetail>
                <strong>{result.name}</strong>
                {result.belt && <> &middot; Faixa: {result.belt}</>}
                {result.academy && <> &middot; {result.academy.name}</>}
                {result.expiresAt && (
                  <>
                    {" "}
                    &middot; Válida até:{" "}
                    <span translate="no">
                      {new Date(result.expiresAt).toLocaleDateString("pt-BR")}
                    </span>
                  </>
                )}
              </ResultDetail>
              {syncedFields.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                  <Typography sx={{ fontSize: 12, color: "#15803d" }}>
                    {syncedFields.join(" e ")} {syncedFields.length === 1 ? "atualizada" : "atualizados"} conforme a federação
                  </Typography>
                </Box>
              )}
            </ResultContent>
          </ResultBox>
        </>
      )}

      {/* NOT_FOUND — Atleta não filiado, oferecer filiação */}
      {status === "not_found" && (
        <>
          <ResultBox $variant="warning">
            <WarningAmberIcon sx={{ fontSize: 22, color: "#d97706", mt: "2px" }} />
            <ResultContent>
              <ResultTitle>Atleta não filiado</ResultTitle>
              <ResultDetail>
                Não foi encontrada filiação ativa para este CPF. Para se inscrever
                neste torneio, é necessário se filiar à federação.
              </ResultDetail>
            </ResultContent>
          </ResultBox>
          <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowAffiliationForm(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--navy, #1a2252)",
                "&:hover": { bgcolor: "var(--navy-light, #243070)" },
              }}
            >
              Filiar-se agora
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                autoVerifiedRef.current = null;
                setCpf("");
                setManualCpfMode(true);
                reset();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#d97706",
                borderColor: "#d97706",
                "&:hover": { borderColor: "#b45309", bgcolor: "#fffbeb" },
              }}
            >
              Verificar outro CPF
            </Button>
          </Box>
        </>
      )}

      {/* NOT_ACTIVE — Diferenciar por sub-status */}
      {status === "not_active" && (
        <>
          {affiliationSubStatus === "AWAITING_PAYMENT" && result?.affiliationId ? (
            <>
              <ResultBox $variant="warning">
                <WarningAmberIcon sx={{ fontSize: 22, color: "#d97706", mt: "2px" }} />
                <ResultContent>
                  <ResultTitle>Filiação aguardando pagamento</ResultTitle>
                  <ResultDetail>
                    Existe uma filiação pendente de pagamento para este CPF.
                    Conclua o pagamento para ativar sua filiação.
                  </ResultDetail>
                </ResultContent>
              </ResultBox>
              {!showAffiliationForm && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setShowAffiliationForm(true)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: "var(--navy, #1a2252)",
                      "&:hover": { bgcolor: "var(--navy-light, #243070)" },
                    }}
                  >
                    Realizar pagamento
                  </Button>
                </Box>
              )}
            </>
          ) : affiliationSubStatus === "PENDING" ? (
            <ResultBox $variant="info">
              <HourglassTopIcon sx={{ fontSize: 22, color: "#1e40af", mt: "2px" }} />
              <ResultContent>
                <ResultTitle>Filiação em análise</ResultTitle>
                <ResultDetail>
                  Sua filiação está sendo analisada pela federação. Assim que for
                  aprovada, você poderá prosseguir com a inscrição.
                </ResultDetail>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <CircularProgress size={14} sx={{ color: "#1e40af" }} />
                  <Typography sx={{ fontSize: 12, color: "#1e40af" }}>
                    Verificando status automaticamente...
                  </Typography>
                </Box>
              </ResultContent>
            </ResultBox>
          ) : affiliationSubStatus === "REJECTED" ? (
            <ResultBox $variant="error">
              <BlockIcon sx={{ fontSize: 22, color: "#dc2626", mt: "2px" }} />
              <ResultContent>
                <ResultTitle>Filiação rejeitada</ResultTitle>
                <ResultDetail>
                  Sua solicitação de filiação foi rejeitada pela federação.
                  Entre em contato com a federação para mais informações.
                </ResultDetail>
              </ResultContent>
            </ResultBox>
          ) : affiliationSubStatus === "SUSPENDED" ? (
            <ResultBox $variant="error">
              <BlockIcon sx={{ fontSize: 22, color: "#dc2626", mt: "2px" }} />
              <ResultContent>
                <ResultTitle>Filiação suspensa</ResultTitle>
                <ResultDetail>
                  A filiação deste atleta foi suspensa pela federação.
                  Não é possível se inscrever neste torneio. Entre em contato com a {federationName} para regularizar.
                </ResultDetail>
              </ResultContent>
            </ResultBox>
          ) : affiliationSubStatus === "CANCELLED" ? (
            <ResultBox $variant="error">
              <BlockIcon sx={{ fontSize: 22, color: "#dc2626", mt: "2px" }} />
              <ResultContent>
                <ResultTitle>Filiação cancelada</ResultTitle>
                <ResultDetail>
                  A filiação deste atleta foi cancelada. Entre em contato com a {federationName} para solicitar nova filiação.
                </ResultDetail>
              </ResultContent>
            </ResultBox>
          ) : (
            <>
              <ResultBox $variant="warning">
                <WarningAmberIcon sx={{ fontSize: 22, color: "#d97706", mt: "2px" }} />
                <ResultContent>
                  <ResultTitle>Filiação inativa</ResultTitle>
                  <ResultDetail>
                    {errorMessage ||
                      "A filiação deste atleta não está ativa. Entre em contato com a federação."}
                  </ResultDetail>
                </ResultContent>
              </ResultBox>
            </>
          )}
        </>
      )}

      {/* EXPIRED */}
      {status === "expired" && (
        <>
          <ResultBox $variant="warning">
            <WarningAmberIcon sx={{ fontSize: 22, color: "#d97706", mt: "2px" }} />
            <ResultContent>
              <ResultTitle>Filiação expirada</ResultTitle>
              <ResultDetail>
                A filiação deste atleta expirou. Renove sua filiação para prosseguir.
              </ResultDetail>
            </ResultContent>
          </ResultBox>
          <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowAffiliationForm(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--navy, #1a2252)",
                "&:hover": { bgcolor: "var(--navy-light, #243070)" },
              }}
            >
              Renovar filiação
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                autoVerifiedRef.current = null;
                setCpf("");
                setManualCpfMode(true);
                reset();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#d97706",
                borderColor: "#d97706",
                "&:hover": { borderColor: "#b45309", bgcolor: "#fffbeb" },
              }}
            >
              Verificar outro CPF
            </Button>
          </Box>
        </>
      )}

      {status === "error" && (
        <>
          <ResultBox $variant="error">
            <ErrorOutlineIcon sx={{ fontSize: 22, color: "#dc2626", mt: "2px" }} />
            <ResultContent>
              <ResultTitle>Erro na verificação</ResultTitle>
              <ResultDetail>
                {errorMessage || "Erro ao verificar filiação. Tente novamente."}
              </ResultDetail>
            </ResultContent>
          </ResultBox>
          <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleVerify}
              disabled={false}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--navy, #1a2252)",
                "&:hover": { bgcolor: "var(--navy-light, #243070)" },
              }}
            >
              Tentar novamente
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setCpf("");
                setShowAffiliationForm(true);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--navy, #1a2252)",
                "&:hover": { bgcolor: "var(--navy-light, #243070)" },
              }}
            >
              Filiar-se agora
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                autoVerifiedRef.current = null;
                setCpf("");
                setManualCpfMode(true);
                reset();
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#d97706",
                borderColor: "#d97706",
                "&:hover": { borderColor: "#b45309", bgcolor: "#fffbeb" },
              }}
            >
              Verificar outro CPF
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};

export default FederationVerificationBox;
